/*
 * label-maker/pdf.js — the sheet as an exact-millimetre PDF.
 *
 * WHY PDF AND NOT window.print()
 * ──────────────────────────────
 * An HTML page has no physical size. Printing one goes through the page box,
 * the @page rule, the browser's own margins and finally the driver's scaling,
 * and each of those can and does resize it. A PDF page box is absolute: 1 pt is
 * 1/72 inch by definition, so a label drawn at 38.1 mm is 38.1 mm in the file.
 * What remains is the driver, which is why the sheet carries a printed 50 mm
 * bar (see `rulerSlot`): the user can prove the scale after printing instead of
 * trusting a checkbox they may not have seen.
 *
 * COORDINATES. Everything upstream speaks millimetres with y pointing DOWN from
 * the top-left of the page, because that is how a sheet of labels is described
 * and how people read a grid. PDF is points, y UP from the bottom-left. The
 * flip happens here and nowhere else — one place to get right, one place to
 * test.
 *
 * TEXT. Via PdfTextFont, which embeds a subset of a face that can actually
 * render the string. pdf-lib's built-in fonts are WinAnsi and throw on the
 * first Cyrillic character; an embedded face that lacks a glyph silently draws
 * a blank box and still reports a plausible width. Both failure modes are that
 * module's whole reason to exist, so this file does not second-guess it.
 *
 * BARCODES. Rendered by bwip-js at an integer module scale so every bar edge
 * lands on a pixel boundary, then embedded as PNG at >= 600 dpi effective.
 * Vector would be marginally sharper; a scan test is the thing that actually
 * matters and is what tests/playwright/label-maker-pdf.mjs performs on the
 * generated file.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.LabelPdf = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var PT = 72 / 25.4;                  // exact: points per millimetre

    /* Both libraries ARE the tool — no pdf-lib, no PDF; no bwip-js, no barcode.
     * So they go through cdn_chain(): OUR origin first on every locale, an
     * external mirror only as a fallback. A hard-coded '/vendor/...' happened to
     * be right for RU and was right by accident; a `cdn()` call would have been
     * wrong, because it picks by LOCALE and would send a Russian visitor reading
     * the English page to jsdelivr — unreachable there means a dead tool, not a
     * slower one. The lists come from the page (see the blade).
     * Guard for presence on both origins: CdnChainAssetsPresentTest. */
    function assetChainFor(name, fallbackPath) {
        var cfg = (window.__LABEL_MAKER__ && window.__LABEL_MAKER__.assets) || {};
        var list = cfg[name];
        return (list && list.length) ? list : [fallbackPath];
    }

    /* Effective resolution for rasterised parts (barcodes, photos). 600 dpi is
     * a laser printer's native grid; going higher only inflates the file. */
    var RASTER_DPI = 600;

    var _pdfLib = null, _bwip = null;

    /* window.__assetChain walks the list in order and reports to telemetry when
     * a mirror had to be used — a tool limping on a fallback teaches nobody
     * unless it says so. Falls back to a plain <script> only if the shared
     * helper is somehow absent, so a missing global cannot kill the export. */
    function loadChain(name, fallbackPath, probe, label) {
        if (probe()) return Promise.resolve(probe());
        var urls = assetChainFor(name, fallbackPath);
        var p = (window.__assetChain && window.__assetChain.loadScript)
            ? window.__assetChain.loadScript(urls, label)
            : new Promise(function (resolve, reject) {
                var s = document.createElement('script');
                s.src = urls[0];
                s.async = true;
                s.onload = resolve;
                s.onerror = function () { reject(new Error(label + ' failed to load')); };
                document.head.appendChild(s);
            });
        return p.then(function () {
            var got = probe();
            if (!got) throw new Error(label + ' loaded but absent');
            return got;
        });
    }
    function ensurePdfLib() {
        if (_pdfLib) return Promise.resolve(_pdfLib);
        return loadChain('pdfLib', '/vendor/pdf-lib/pdf-lib.min.js',
            function () { return window.PDFLib; }, 'pdf-lib')
            .then(function (l) { _pdfLib = l; return l; });
    }
    function ensureBwip() {
        if (_bwip) return Promise.resolve(_bwip);
        return loadChain('bwip', '/vendor/bwip-js/bwip-js-min.js',
            function () { return window.bwipjs; }, 'bwip-js')
            .then(function (l) { _bwip = l; return l; });
    }

    /* ── colour ──────────────────────────────────────────────────────────── */
    function rgb(PL, hex) {
        var h = String(hex || '#000000').replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        var n = parseInt(h, 16);
        if (!isFinite(n)) return PL.rgb(0, 0, 0);
        return PL.rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
    }

    /* Every page here is drawn at its real size in millimetres — a label, a
     * sheet of die-cuts, a test ruler — and «fit to page» in the print dialog
     * ruins all of them (modules off the dot grid, labels off the cuts). The
     * PDF says so itself: ViewerPreferences /PrintScaling /None — by the PDF
     * spec the viewer's print dialog opens on actual size (Acrobat honours it).
     * Metadata only; a viewer that ignores it prints as before (23.10.2026). */
    function noPrintScaling(doc, PL) {
        try {
            if (PL.PrintScaling && doc.catalog && doc.catalog.getOrCreateViewerPreferences) {
                doc.catalog.getOrCreateViewerPreferences().setPrintScaling(PL.PrintScaling.None);
            }
        } catch (e) { /* an older pdf-lib: the page sizes still say it */ }
    }

    /* ── the y-flip, in one place ────────────────────────────────────────── */
    function makeFrame(pageHmm) {
        return {
            x: function (mm) { return mm * PT; },
            /* Top-down mm → PDF points from the bottom. `h` is the height of the
             * thing being placed, because pdf-lib anchors rectangles and images
             * at their BOTTOM-left while our model anchors at the top-left. */
            y: function (mm, h) { return (pageHmm - mm - (h || 0)) * PT; },
        };
    }

    /* ── barcodes ────────────────────────────────────────────────────────── */
    function barcodePng(it) {
        return ensureBwip().then(function (bwip) {
            var canvas = document.createElement('canvas');
            // Integer scale keeps module edges on pixel boundaries; without it
            // resampling softens the quiet zone and marginal scanners fail.
            var wMm = it.box ? it.box.w : 20;
            var targetPx = Math.max(64, Math.round(wMm / 25.4 * RASTER_DPI));
            var scale = Math.max(1, Math.round(targetPx / Math.max(1, wMm / 25.4 * 72)));
            // Options come from render.js so the screen and the sheet ask
            // bwip-js for the SAME picture; only the raster scale differs.
            var opts = window.LabelRender.barcodeOptions(it, scale);
            bwip.toCanvas(canvas, opts);
            return new Promise(function (res, rej) {
                canvas.toBlob(function (b) { b ? res(b) : rej(new Error('barcode canvas empty')); }, 'image/png');
            });
        }).then(function (blob) { return blob.arrayBuffer(); })
            .then(function (buf) { return new Uint8Array(buf); });
    }

    /* ── the scale-verification bar ──────────────────────────────────────────
     * A 50 mm line with 10 mm ticks and a printed caption, placed in the sheet's
     * own margin — backing paper that gets thrown away. After printing, a ruler
     * against this line answers "did the driver rescale me?" in two seconds. It
     * is the only honest way to close the loop, because nothing inside the
     * browser can observe what the driver did. */
    function drawRuler(page, PL, frame, slot, caption, font) {
        var y = slot.y, x = slot.x, len = slot.len;
        var col = PL.rgb(0.45, 0.45, 0.45);
        page.drawLine({
            start: { x: frame.x(x), y: frame.y(y + 4) },
            end: { x: frame.x(x + len), y: frame.y(y + 4) },
            thickness: 0.35, color: col,
        });
        for (var mm = 0; mm <= len; mm += 10) {
            var tall = (mm % 50 === 0);
            page.drawLine({
                start: { x: frame.x(x + mm), y: frame.y(y + 4) },
                end: { x: frame.x(x + mm), y: frame.y(y + (tall ? 0.8 : 2)) },
                thickness: 0.35, color: col,
            });
        }
        if (font && caption) {
            var size = 4.6;
            page.drawText(caption, {
                x: frame.x(x + len + 2.5), y: frame.y(y + 3.6),
                size: size, font: font, color: col,
            });
        }
    }

    /* ── cut marks for plain paper ───────────────────────────────────────────
     * When there is no die cut, the user cuts by hand — and needs marks OUTSIDE
     * the label so the blade has something to follow without crossing the
     * artwork. */
    function cutMarks(page, PL, frame, x, y, w, h) {
        var L = 2, O = 0.8, col = PL.rgb(0.6, 0.6, 0.6), t = 0.25;
        function seg(x1, y1, x2, y2) {
            page.drawLine({
                start: { x: frame.x(x1), y: frame.y(y1) },
                end: { x: frame.x(x2), y: frame.y(y2) },
                thickness: t, color: col,
            });
        }
        seg(x - O - L, y, x - O, y);                 seg(x, y - O - L, x, y - O);
        seg(x + w + O, y, x + w + O + L, y);         seg(x + w, y - O - L, x + w, y - O);
        seg(x - O - L, y + h, x - O, y + h);         seg(x, y + h + O, x, y + h + O + L);
        seg(x + w + O, y + h, x + w + O + L, y + h); seg(x + w, y + h + O, x + w, y + h + O + L);
    }

    function labelOutline(page, PL, frame, cell, fmt, color, width) {
        if (fmt.shape === 'circle' || fmt.shape === 'ellipse') {
            page.drawEllipse({
                x: frame.x(cell.x + cell.w / 2),
                y: frame.y(cell.y + cell.h / 2),
                xScale: cell.w / 2 * PT, yScale: cell.h / 2 * PT,
                borderColor: color, borderWidth: width, opacity: 0,
            });
        } else {
            page.drawRectangle({
                x: frame.x(cell.x), y: frame.y(cell.y, cell.h),
                width: cell.w * PT, height: cell.h * PT,
                borderColor: color, borderWidth: width, opacity: 0,
                // pdf-lib has no per-corner radius; a rounded die cut is drawn
                // square here on purpose — this line is a GUIDE, and pretending
                // to show a radius we cannot draw accurately would mislead.
            });
        }
    }

    /* ══════════════════════════════════════════════════════════════════════
     * build(job) -> { blob, pages, ... }
     *
     * job = {
     *   fmt,                        // a LabelSheets format
     *   correction,                 // {scale,dx,dy}
     *   cells: [ {items:[...]} | null ],   // laid-out labels, from layout.js
     *   order: 'row'|'col',
     *   outline: bool,              // draw the die-cut guide
     *   cutMarks: bool,             // corner ticks for scissors
     *   ruler: bool,                // the 50 mm scale check
     *   rulerCaption: string,
     *   background: '#ffffff'|null,
     * }
     * ══════════════════════════════════════════════════════════════════════ */
    function build(job) {
        var Sheets = (typeof window !== 'undefined' && window.LabelSheets) || null;
        if (!Sheets) return Promise.reject(new Error('LabelSheets not loaded'));

        var fmt = job.fmt;
        var pageMm = Sheets.page(fmt.page);
        var perSheet = Sheets.perSheet(fmt);
        var cells = job.cells || [];
        var pageCount = Math.max(1, Math.ceil(cells.length / perSheet));

        return ensurePdfLib().then(function (PL) {
            return PL.PDFDocument.create().then(function (doc) {
                doc.setCreator('Timbrica label-maker');
                noPrintScaling(doc, PL);
                doc.setProducer('Timbrica');
                var frame = makeFrame(pageMm.h);

                // Collect every string that will be drawn so the right faces
                // are embedded once, before any page is written.
                return prepareFonts(doc, cells, job).then(function (fonts) {
                    return prepareImages(doc, cells).then(function (images) {
                        return prepareBarcodes(doc, cells).then(function (codes) {
                            var slot = job.ruler ? Sheets.rulerSlot(fmt) : null;

                            for (var p = 0; p < pageCount; p++) {
                                var page = doc.addPage([pageMm.w * PT, pageMm.h * PT]);
                                if (job.background) {
                                    page.drawRectangle({
                                        x: 0, y: 0, width: pageMm.w * PT, height: pageMm.h * PT,
                                        color: rgb(PL, job.background),
                                    });
                                }
                                for (var i = 0; i < perSheet; i++) {
                                    var idx = p * perSheet + i;
                                    if (idx >= cells.length) break;
                                    var cell = Sheets.cellAt(fmt, i, {
                                        order: job.order, correction: job.correction,
                                    });
                                    var content = cells[idx];
                                    if (job.outline) {
                                        labelOutline(page, PL, frame, cell, fmt, PL.rgb(0.78, 0.78, 0.78), 0.25);
                                    }
                                    if (job.cutMarks) {
                                        cutMarks(page, PL, frame, cell.x, cell.y, cell.w, cell.h);
                                    }
                                    if (content) {
                                        drawCell(page, PL, frame, cell, content, fonts, images, codes,
                                            Sheets.correction(job.correction).scale);
                                    }
                                }
                                if (slot) {
                                    drawRuler(page, PL, frame, slot, job.rulerCaption || '50 mm',
                                        fonts.__caption || null);
                                }
                            }

                            return doc.save({ useObjectStreams: true }).then(function (bytes) {
                                return {
                                    blob: new Blob([bytes], { type: 'application/pdf' }),
                                    bytes: bytes,
                                    pages: pageCount,
                                    perSheet: perSheet,
                                    cells: cells.length,
                                    pageWmm: pageMm.w,
                                    pageHmm: pageMm.h,
                                };
                            });
                        });
                    });
                });
            });
        });
    }

    /* Embed one face per (font, weight) pair actually used, over the union of
     * every string on the sheet — so subsetting keeps all needed glyphs. */
    function prepareFonts(doc, cells, job) {
        var TF = (typeof window !== 'undefined') ? window.PdfTextFont : null;
        var need = {};
        cells.forEach(function (c) {
            if (!c) return;
            (c.items || []).forEach(function (it) {
                if (it.kind !== 'text') return;
                var key = (it.font || 'noto') + '|' + (it.bold ? 'b' : 'r');
                need[key] = (need[key] || '') + it.lines.join('');
            });
        });
        // The ruler caption is Latin and always present when the ruler is on.
        if (job && job.ruler) need['noto|r'] = (need['noto|r'] || '') + (job.rulerCaption || '50 mm');

        var keys = Object.keys(need);
        if (!keys.length) return Promise.resolve({});
        if (!TF) return Promise.reject(new Error('PdfTextFont not loaded'));

        return TF.ready(keys.map(function (k) { return need[k]; }).join('')).then(function () {
            return Promise.all(keys.map(function (k) {
                var parts = k.split('|');
                var faceId = parts[0], bold = parts[1] === 'b';
                // A face that cannot render the text is replaced by one that
                // can, rather than drawing a row of blank boxes.
                /* Arguments are (text, faceId). They were the other way round
                   until 2026-08-20, and `missing()` answers [] for a face it
                   does not know — so the swapped call said "yes, this face can
                   render it" for EVERY string, and the substitution below never
                   once fired. Measured: a Korean label printed tofu boxes while
                   the Korean face sat unused in the table. */
                var usable = TF.canRender(need[k], faceId) ? faceId : TF.pick(need[k]);
                return TF.embed(doc, usable, { bold: bold, subset: true })
                    .then(function (f) {
                        /* ⚠️ `faceMeta`, NOT `face`. `face` is the HEAVY accessor —
                           it returns a PROMISE for a parsed fontkit face — so
                           `.boldUrl` was always undefined and `faux` below was
                           always true: every face that HAS a real Bold file got
                           the fake one stroked on top of it. Measured on prod
                           2026-08-23: the six faces WITH a bold file all printed
                           23–37 % more ink than the preview; the five without one
                           were correct. See pdf-text-font.js — `face` was declared
                           twice in the same export literal and the light lookup
                           was shadowed. */
                        var face = TF.faceMeta(usable) || {};
                        return {
                            key: k, font: f, faceId: usable,
                            // Bold asked for, no bold FILE to give: the browser
                            // fakes it on screen, so the PDF has to fake it too
                            // or the two disagree. See drawFauxBold below.
                            faux: bold && !face.boldUrl,
                        };
                    });
            }));
        }).then(function (list) {
            var out = {}, faux = {};
            list.forEach(function (e) { out[e.key] = e.font; if (e.faux) faux[e.key] = true; });
            out.__caption = out['noto|r'] || list[0].font;
            out.__faux = faux;
            return out;
        });
    }

    function prepareImages(doc, cells) {
        var srcs = {};
        cells.forEach(function (c) {
            if (!c) return;
            (c.items || []).forEach(function (it) { if (it.kind === 'image' && it.src) srcs[it.src] = 1; });
        });
        var keys = Object.keys(srcs);
        if (!keys.length) return Promise.resolve({});
        return Promise.all(keys.map(function (src) {
            return fetch(src).then(function (r) { return r.arrayBuffer(); }).then(function (buf) {
                var bytes = new Uint8Array(buf);
                var isPng = bytes[0] === 0x89 && bytes[1] === 0x50;
                return (isPng ? doc.embedPng(bytes) : doc.embedJpg(bytes))
                    .then(function (img) { return { src: src, img: img }; });
            }).catch(function () { return null; });
        })).then(function (list) {
            var out = {};
            list.forEach(function (e) { if (e) out[e.src] = e.img; });
            return out;
        });
    }

    /* One embedded image per (symbology, value, size) — a sheet of 65 identical
     * QR codes must not carry 65 copies of the same PNG. */
    function prepareBarcodes(doc, cells) {
        var need = {};
        cells.forEach(function (c) {
            if (!c) return;
            (c.items || []).forEach(function (it) {
                if (it.kind !== 'barcode' || !String(it.value || '').length) return;
                var key = it.matrix ? matrixKey(it) : barcodeKey(it);
                need[key] = it;
            });
        });
        var keys = Object.keys(need);
        if (!keys.length) return Promise.resolve({});
        return Promise.all(keys.map(function (k) {
            var it = need[k];
            /* A dot-sized code is not a picture at all: its modules are drawn
               as rectangles in drawCell. Encoding once per value here keeps a
               500-label run at 500 encodes, not 500 per page. */
            if (it.matrix) {
                return ensureBwip().then(function (bwip) {
                    return { key: k, enc: window.LabelMatrix.encode(bwip, it) };
                }).catch(function (e) { return { key: k, enc: null, error: e.message }; });
            }
            return barcodePng(it)
                .then(function (png) { return doc.embedPng(png); })
                .then(function (img) { return { key: k, img: img }; })
                .catch(function (e) { return { key: k, img: null, error: e.message }; });
        })).then(function (list) {
            var out = {};
            list.forEach(function (e) { out[e.key] = e; });
            return out;
        });
    }

    function round2(v) { return Math.round(v * 100) / 100; }

    /* Shared with the preview so both caches key on the same identity. */
    function barcodeKey(it) { return window.LabelRender.barcodeKey(it); }
    function matrixKey(it) { return 'mx|' + window.LabelMatrix.key(it); }

    /* Draw a dot-sized code: the same geometry and the same snapped place the
     * preview draws (LabelMatrix.geometry + place, origin = the cell corner on
     * the page), as rectangles on whole-dot edges. `sx` — the printer
     * correction — scales it like any other content; at 1 every edge is on
     * the dot grid. */
    function drawMatrix(page, PL, frame, cell, it, enc, sx, color) {
        var LM = window.LabelMatrix;
        var geom = LM.geometry(it, enc);
        var pl = LM.place(it, geom, { x: cell.x, y: cell.y });
        var rr = LM.rects(enc, pl);
        for (var i = 0; i < rr.length; i++) {
            var r = rr[i];
            rr[i] = [cell.x + r[0] * sx, cell.y + r[1] * sx, r[2] * sx, r[3] * sx];
        }
        fillRects(page, PL, frame, rr, color);
        return { geom: geom, place: pl };
    }

    /* `sx` is the printer correction, and it has to reach the CONTENT, not just
     * the cell. A correction of 1.03 means "draw everything 3 % larger so the
     * driver's shrink lands it right"; scaling only the cell rectangle and the
     * positions would leave the type its original size inside a stretched box —
     * the labels would move correctly and print at the wrong size. Until
     * 2026-08-19 this read `cell.w / cell.w`, which is 1 by construction. */
    function drawCell(page, PL, frame, cell, content, fonts, images, codes, sx) {
        sx = (typeof sx === 'number' && isFinite(sx) && sx > 0) ? sx : 1;
        (content.items || []).forEach(function (it) {
            var bx = cell.x + it.box.x * sx;
            var by = cell.y + it.box.y * sx;
            var bw = it.box.w * sx, bh = it.box.h * sx;

            if (it.kind === 'shape') {
                var opt = {
                    x: frame.x(bx), y: frame.y(by, bh),
                    width: bw * PT, height: bh * PT,
                };
                if (it.fill) opt.color = rgb(PL, it.fill);
                if (it.stroke) { opt.borderColor = rgb(PL, it.stroke); opt.borderWidth = it.strokeWidth * PT; }
                if (!it.fill) opt.opacity = 0;
                if (it.shape === 'circle' || it.shape === 'ellipse') {
                    page.drawEllipse({
                        x: frame.x(bx + bw / 2), y: frame.y(by + bh / 2),
                        xScale: bw / 2 * PT, yScale: bh / 2 * PT,
                        color: it.fill ? rgb(PL, it.fill) : undefined,
                        borderColor: it.stroke ? rgb(PL, it.stroke) : undefined,
                        borderWidth: it.stroke ? it.strokeWidth * PT : 0,
                        opacity: it.fill ? 1 : 0,
                    });
                } else if (it.shape === 'line') {
                    page.drawLine({
                        start: { x: frame.x(bx), y: frame.y(by + bh / 2) },
                        end: { x: frame.x(bx + bw), y: frame.y(by + bh / 2) },
                        thickness: Math.max(0.1, it.strokeWidth * PT),
                        color: rgb(PL, it.stroke || it.fill || '#000000'),
                    });
                } else {
                    page.drawRectangle(opt);
                }
                return;
            }

            if (it.kind === 'image') {
                var img = images[it.src];
                if (!img) return;
                var fitted = fitBox(img.width / img.height, bw, bh, it.fit);
                page.drawImage(img, {
                    x: frame.x(bx + fitted.dx), y: frame.y(by + fitted.dy, fitted.h),
                    width: fitted.w * PT, height: fitted.h * PT,
                });
                return;
            }

            if (it.kind === 'barcode' && it.matrix) {
                var mx = codes[matrixKey(it)];
                if (!mx || !mx.enc) return;
                drawMatrix(page, PL, frame, cell, it, mx.enc, sx, rgb(PL, it.color));
                return;
            }

            if (it.kind === 'barcode') {
                var entry = codes[barcodeKey(it)];
                if (!entry || !entry.img) return;
                var bfit = fitBox(entry.img.width / entry.img.height, bw, bh, 'contain');
                page.drawImage(entry.img, {
                    x: frame.x(bx + bfit.dx), y: frame.y(by + bfit.dy, bfit.h),
                    width: bfit.w * PT, height: bfit.h * PT,
                });
                return;
            }

            if (it.kind === 'text') {
                var fkey = (it.font || 'noto') + '|' + (it.bold ? 'b' : 'r');
                var font = fonts[fkey] || fonts.__caption;
                if (!font) return;
                /* Five of the faces we offer ship one weight only — Russo One,
                   Yeseva One, Lobster, Marck Script, Pacifico are single-weight
                   by design, there is no bold file to embed. The browser
                   synthesises bold anyway, so before 2026-08-20 the preview
                   showed a bold line and the PDF printed a regular one:
                   measured ink ratios PDF/screen of 0.83, 0.75, 0.78, 0.65,
                   0.80 — the widest miss on the whole page, and silent.

                   The browser's synthesis is a stroke around the fill. Measured
                   on all five at 40/100/200 px, the glyph grows by 0.04–0.05 of
                   the type size in width and the ADVANCE does not move at all —
                   Skia strokes the outline and leaves the metrics alone. PDF can
                   do exactly that: text render mode 2 is fill-then-stroke, so
                   the same stroke reproduces the letter rather than approximating
                   it with a smear.

                   The divisor is not tuned by eye. Drawing at size/24 overshot,
                   so the width that WOULD match was solved from the ink each
                   side actually adds, independently for each of the five faces:
                   31.6, 31.6, 31.9, 31.8, 31.9. Five different outlines
                   recovering one number is the number being real — and it is
                   the far end of Skia's fake-bold ramp (1/24 at ~9 px easing to
                   1/32 above ~36 px), which is where print-resolution type
                   always sits. */
                var faux = !!(fonts.__faux && fonts.__faux[fkey]);
                var size = it.size * sx;
                var ls = (it.letterSpacing || 0) * sx;
                var lineH = it.lineHeight * sx;
                var block = it.lines.length * lineH;
                var top = by;
                if (it.valign === 'middle') top = by + (bh - block) / 2;
                else if (it.valign === 'bottom') top = by + bh - block;

                for (var li = 0; li < it.lines.length; li++) {
                    var line = it.lines[li];
                    if (line === '') continue;
                    /* Letter spacing existed in the layout and in the preview
                       and NOWHERE here, so a spaced-out preset previewed spaced
                       and printed tight: measured 2026-08-20, extent 45.71 mm on
                       screen against 40.71 in the PDF at 1 mm of spacing, and
                       two shipped presets use it. The PDF operator for it is Tc.
                       The extent grows by the gap between letters, one fewer
                       than the letters — the trailing gap adds advance, not ink,
                       and centring must use the ink. */
                    /* ⚠️ A LINE IS NOT ALWAYS ONE DIRECTION. fontkit orders an
                       Arabic line correctly by itself, but digits and Latin
                       inside it get reversed along with it — an address that
                       reads «١٢» on screen printed «٢١». Splitting the line into
                       directional pieces and drawing each one at its own x is
                       the fix; it lives in PdfTextFont because the certificate
                       and card exporters need the very same rule. A line with
                       no right-to-left letter comes back as ONE piece and this
                       arithmetic collapses to what it was. */
                    var pieces = window.PdfTextFont.bidiRuns(line).map(function (r) {
                        var t = window.PdfTextFont.runText(r);
                        /* With Tc set, EVERY glyph carries the extra gap, the
                           last one included — so the step to the next piece
                           includes a trailing gap that the ink does not use. */
                        return { t: t, adv: font.widthOfTextAtSize(t, size * PT) / PT + ls * t.length };
                    });
                    var wMm = pieces.reduce(function (a, r) { return a + r.adv; }, 0) - (ls ? ls : 0);
                    var lx = bx;
                    if (it.align === 'center') lx = bx + (bw - wMm) / 2;
                    else if (it.align === 'right') lx = bx + bw - wMm;
                    // Line breaking was decided upstream from canvas metrics,
                    // which apply kerning; pdf-lib sums raw advances and can
                    // come out a fraction wider. Centring on the wider number
                    // would push the line off the left edge of its box, so the
                    // start is clamped. The line may then reach the right edge
                    // — inside the label, which is the property that matters.
                    if (lx < bx) lx = bx;
                    // Baseline offset rides on the item — measured once in
                    // layout.js from the face's own ascent. Asking pdf-lib for
                    // its own ascent here is exactly what printed every line
                    // ~0.29 em below where the preview showed it (Noto Sans
                    // ascent is 1.07 em against the renderer's hard-coded 0.78),
                    // i.e. 4 mm on a 14 mm type size.
                    var baseline = top + li * lineH + size * (it.ascentEm || 0.8);
                    if (ls) {
                        page.pushOperators(PL.pushGraphicsState(), PL.setCharacterSpacing(ls * PT));
                    }
                    if (faux) {
                        page.pushOperators(
                            PL.pushGraphicsState(),
                            PL.setLineWidth(size * PT / 32),
                            PL.setStrokingColor(rgb(PL, it.color)),
                            PL.setTextRenderingMode(PL.TextRenderingMode.FillAndOutline)
                        );
                    }
                    var px = lx;
                    for (var pi = 0; pi < pieces.length; pi++) {
                        page.drawText(pieces[pi].t, {
                            x: frame.x(px), y: frame.y(baseline),
                            size: size * PT, font: font, color: rgb(PL, it.color),
                        });
                        px += pieces[pi].adv;
                    }
                    if (faux) page.pushOperators(PL.popGraphicsState());
                    if (ls) page.pushOperators(PL.popGraphicsState());
                }
            }
        });
    }

    function fitBox(ratio, bw, bh, mode) {
        if (!isFinite(ratio) || ratio <= 0) return { w: bw, h: bh, dx: 0, dy: 0 };
        var w, h;
        if (mode === 'cover') {
            if (bw / bh > ratio) { w = bw; h = bw / ratio; } else { h = bh; w = bh * ratio; }
        } else {
            if (bw / bh > ratio) { h = bh; w = bh * ratio; } else { w = bw; h = bw / ratio; }
        }
        return { w: w, h: h, dx: (bw - w) / 2, dy: (bh - h) / 2 };
    }

    /* ══════════════════════════════════════════════════════════════════════
     * The overlay check sheet.
     *
     * THIS IS THE ROOT-CAUSE TOOL. It prints the grid outlines and nothing
     * else, on PLAIN paper. Hold it against the real label sheet up to a
     * window: if the printed rectangles sit on the die cuts, the geometry AND
     * the driver scale AND the feed offset are all correct — three unknowns
     * settled by one look, before a single sheet of label stock is risked.
     *
     * Around the edges it prints a millimetre scale so a mismatch can be READ
     * rather than guessed, and the numbers typed straight back into the offset
     * fields.
     * ══════════════════════════════════════════════════════════════════════ */
    function buildOverlay(fmt, opts) {
        opts = opts || {};
        var Sheets = window.LabelSheets;
        var pageMm = Sheets.page(fmt.page);

        return ensurePdfLib().then(function (PL) {
            return PL.PDFDocument.create().then(function (doc) {
                doc.setCreator('Timbrica label-maker');
                noPrintScaling(doc, PL);
                var TF = window.PdfTextFont;
                var caption = [opts.title || '', opts.hint || ''].join(' ');
                return TF.ready(caption + '0123456789 mm').then(function () {
                    return TF.embed(doc, TF.canRender(caption, 'noto') ? 'noto' : TF.pick(caption), { subset: true });
                }).then(function (font) {
                    var page = doc.addPage([pageMm.w * PT, pageMm.h * PT]);
                    var frame = makeFrame(pageMm.h);
                    var grey = PL.rgb(0.1, 0.1, 0.1);

                    var perSheet = Sheets.perSheet(fmt);
                    for (var i = 0; i < perSheet; i++) {
                        var cell = Sheets.cellAt(fmt, i, { correction: opts.correction });
                        labelOutline(page, PL, frame, cell, fmt, grey, 0.4);
                    }

                    // Edge scales: 0 at the page corner, ticks every 5 mm, a
                    // number every 10, so an offset is read directly in mm.
                    drawEdgeScale(page, PL, frame, pageMm, font);

                    if (opts.title) {
                        page.drawText(opts.title, {
                            x: frame.x(12), y: frame.y(pageMm.h - 6),
                            size: 8, font: font, color: PL.rgb(0.35, 0.35, 0.35),
                        });
                    }
                    return doc.save({ useObjectStreams: true }).then(function (bytes) {
                        return { blob: new Blob([bytes], { type: 'application/pdf' }), bytes: bytes, pages: 1 };
                    });
                });
            });
        });
    }

    /* ══════════════════════════════════════════════════════════════════════
     * The ROLL test label (sheets.rollTestPlan). One page the size of the label:
     * rulers to measure, a patch of one-dot patterns ON the printer's dot grid,
     * an optional sample code and a caption. Printed first, it answers "is my
     * dialog at 100 % and my dpi right?" on ONE label instead of on the run.
     *
     * opts = { dpi, caption, rulerCaption, sample: function(page, PL, frame,
     *          slot) — draws the sample code into the slot (the 2D-code module
     *          owns that), lines: [string] — small text under the caption }
     * ══════════════════════════════════════════════════════════════════════ */
    function buildTestPage(fmt, opts) {
        opts = opts || {};
        var Sheets = window.LabelSheets;
        var plan = Sheets.rollTestPlan(fmt, { dpi: opts.dpi });
        var pageMm = plan.page;
        return ensurePdfLib().then(function (PL) {
            return PL.PDFDocument.create().then(function (doc) {
                doc.setCreator('Timbrica label-maker');
                noPrintScaling(doc, PL);
                doc.setProducer('Timbrica');
                if (opts.title) doc.setTitle(opts.title);
                var TF = window.PdfTextFont;
                var words = [opts.caption || '', opts.rulerCaption || '', (opts.lines || []).join(' '), '0123456789 mm'].join(' ');
                return TF.ready(words).then(function () {
                    return TF.embed(doc, TF.canRender(words, 'noto') ? 'noto' : TF.pick(words), { subset: true });
                }).then(function (font) {
                    var page = doc.addPage([pageMm.w * PT, pageMm.h * PT]);
                    var frame = makeFrame(pageMm.h);
                    var ink = PL.rgb(0, 0, 0);
                    var line = function (x1, y1, x2, y2, t) {
                        page.drawLine({ start: { x: frame.x(x1), y: frame.y(y1) }, end: { x: frame.x(x2), y: frame.y(y2) }, thickness: t || 0.3, color: ink });
                    };

                    // Horizontal ruler: ticks every 5 mm, long ones every 10.
                    var r = plan.ruler, mm;
                    line(r.x, r.y, r.x + r.len, r.y, 0.3);
                    for (mm = 0; mm <= r.len + 1e-9; mm += 5) {
                        line(r.x + mm, r.y, r.x + mm, r.y - (mm % 10 === 0 ? r.tick : r.tick * 0.55), 0.3);
                    }
                    var rc = opts.rulerCaption || (r.len + ' mm');
                    var rcW = font.widthOfTextAtSize(rc, 5) / PT;
                    page.drawText(rc, { x: frame.x(r.x + (r.len - rcW) / 2), y: frame.y(r.y + 2.4), size: 5, font: font, color: ink });

                    if (plan.vruler) {
                        var v = plan.vruler;
                        line(v.x, v.y, v.x, v.y + v.len, 0.3);
                        for (mm = 0; mm <= v.len + 1e-9; mm += 5) {
                            line(v.x, v.y + mm, v.x + (mm % 10 === 0 ? v.tick : v.tick * 0.55), v.y + mm, 0.3);
                        }
                    }

                    drawDotPatch(page, PL, frame, plan.patch);

                    // Caption: as small as is still read on a 25 mm label.
                    var cy = pageMm.h - 1.4, size = 4.4;
                    var lines = [opts.caption || ''].concat(opts.lines || []).filter(Boolean);
                    var capH = lines.length * size / PT * 1.25 + 0.6;

                    /* The sample code, at the module the run will use. Drawn
                       through the very path the labels take (drawMatrix), so
                       what is measured here is what the run prints. */
                    var sampleInfo = null;
                    if (opts.sample && typeof opts.sample === 'object' && opts._sampleEnc) {
                        var s = plan.sample;
                        var sit = {
                            box: { x: s.x, y: s.y, w: s.w, h: Math.max(1, s.h - capH) },
                            dpi: plan.dpi, dots: opts.sample.dots, quietMm: 0,
                            minModuleMm: opts.sample.minModuleMm, maxModuleMm: opts.sample.maxModuleMm,
                            align: 'left', valign: 'top',
                        };
                        sampleInfo = drawMatrix(page, PL, frame, { x: 0, y: 0 }, sit, opts._sampleEnc, 1, ink);
                    } else if (typeof opts.sample === 'function') {
                        opts.sample(page, PL, frame, plan.sample, font);
                    }
                    for (var li = lines.length - 1; li >= 0; li--) {
                        page.drawText(lines[li], { x: frame.x(plan.sample.x), y: frame.y(cy), size: size, font: font, color: ink });
                        cy -= size / PT * 1.25;
                    }
                    return doc.save({ useObjectStreams: true }).then(function (bytes) {
                        return { blob: new Blob([bytes], { type: 'application/pdf' }), bytes: bytes, pages: 1, plan: plan, sample: sampleInfo };
                    });
                });
            });
        });
    }

    /* buildTestPage with a sample object needs the symbol first (bwip-js is
     * async to load). Public entry: resolves the sample, then builds. */
    function buildTestLabel(fmt, opts) {
        opts = opts || {};
        if (!opts.sample || typeof opts.sample !== 'object' || !opts.sample.value) return buildTestPage(fmt, opts);
        return ensureBwip().then(function (bwip) {
            var enc = window.LabelMatrix.encode(bwip, { value: opts.sample.value, gs1: opts.sample.gs1 !== false, symbology: 'gs1datamatrix' });
            var o = {};
            for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k];
            o._sampleEnc = enc;
            return buildTestPage(fmt, o);
        });
    }

    /* Four quadrants of one-dot patterns, every edge ON the dot grid:
     * vertical stripes, horizontal stripes, 1-dot checker, 2-dot checker.
     * Rows of equal dots are merged into one rectangle each, so even a 600-dpi
     * patch stays a few hundred operators. */
    function drawDotPatch(page, PL, frame, patch) {
        var d = patch.dotMm, n = patch.dots, h = n / 2;
        var rects = [];
        function cell(ix, iy, w, hh) { rects.push([patch.x + ix * d, patch.y + iy * d, w * d, hh * d]); }
        var ix, iy;
        for (ix = 0; ix < h; ix += 2) cell(ix, 0, 1, h);                       // ║ stripes
        for (iy = 0; iy < h; iy += 2) cell(h, iy, h, 1);                       // ═ stripes
        for (iy = 0; iy < h; iy++) for (ix = (iy % 2); ix < h; ix += 2) cell(ix, h + iy, 1, 1);           // 1-dot checker
        for (iy = 0; iy < h; iy += 2) for (ix = ((iy / 2) % 2) * 2; ix < h; ix += 4) cell(h + ix, h + iy, 2, 2); // 2-dot checker
        fillRects(page, PL, frame, rects, PL.rgb(0, 0, 0));
        // A one-dot frame round the patch, so a clipped or shifted patch shows.
        var o = [[patch.x - d * 2, patch.y - d * 2, patch.mm + d * 4, d], [patch.x - d * 2, patch.y + patch.mm + d, patch.mm + d * 4, d],
            [patch.x - d * 2, patch.y - d, d, patch.mm + d * 2], [patch.x + patch.mm + d, patch.y - d, d, patch.mm + d * 2]];
        fillRects(page, PL, frame, o, PL.rgb(0, 0, 0));
    }

    /* Rectangles in page millimetres (top-left origin) as raw `re`+`f`
     * operators, in bounded chunks: pdf-lib pushes operators with one
     * `push.apply`, and an unbounded list hits the engine's argument ceiling
     * (docs/why/primitives/pdf-svg-path.md — ~62 000 in Chromium). */
    function fillRects(page, PL, frame, rects, color) {
        if (!rects.length) return;
        var CHUNK = 4000;
        page.pushOperators(PL.pushGraphicsState(), PL.setFillingColor(color));
        for (var i = 0; i < rects.length; i += CHUNK) {
            var ops = [];
            var end = Math.min(rects.length, i + CHUNK);
            for (var k = i; k < end; k++) {
                var r = rects[k];
                ops.push(PL.rectangle(frame.x(r[0]), frame.y(r[1], r[3]), r[2] * PT, r[3] * PT));
            }
            ops.push(PL.fill());
            page.pushOperators.apply(page, ops);
        }
        page.pushOperators(PL.popGraphicsState());
    }

    function drawEdgeScale(page, PL, frame, pageMm, font) {
        var col = PL.rgb(0.45, 0.45, 0.45);
        function tick(x1, y1, x2, y2) {
            page.drawLine({
                start: { x: frame.x(x1), y: frame.y(y1) },
                end: { x: frame.x(x2), y: frame.y(y2) },
                thickness: 0.3, color: col,
            });
        }
        var mm;
        for (mm = 0; mm <= pageMm.w; mm += 5) {
            var big = mm % 10 === 0;
            tick(mm, 0, mm, big ? 4 : 2.2);
            if (big && mm > 0 && mm < pageMm.w - 8) {
                page.drawText(String(mm), { x: frame.x(mm + 0.6), y: frame.y(5.6), size: 4, font: font, color: col });
            }
        }
        for (mm = 0; mm <= pageMm.h; mm += 5) {
            var b2 = mm % 10 === 0;
            tick(0, mm, b2 ? 4 : 2.2, mm);
            if (b2 && mm > 0 && mm < pageMm.h - 8) {
                page.drawText(String(mm), { x: frame.x(5.2), y: frame.y(mm + 1.4), size: 4, font: font, color: col });
            }
        }
    }

    return {
        PT: PT,
        ensureBwip: ensureBwip,
        build: build,
        buildOverlay: buildOverlay,
        buildTestPage: buildTestLabel,
        fillRects: fillRects,
        drawMatrix: drawMatrix,
        barcodePng: barcodePng,
        __makeFrame: makeFrame,
        __fitBox: fitBox,
    };
});
